/**
 * @file resolveDesktopPinScope.ts
 * @description The one narrowing boundary for `pin.meta.scope`. Consumers:
 * `useDesktopPin`, `useDesktopPinHolders`, and every surface that asks how much
 * of a pinned artifact is kept.
 *
 * `Meta` carries an index signature, so `DesktopPinScope` constrains nothing at
 * the field — the value on the document is whatever the last writer put there,
 * and the `file-sync` agent writes it too. That is why the shape is checked
 * here at runtime and why `as DesktopPinScope` must appear nowhere else in the
 * codebase: a cast asserts a guarantee the field cannot give.
 *
 * Every degradation reads as whole-document, matching the contract the agent
 * honors: an absent scope, a non-object, an unknown `kind`, or a `files` scope
 * whose `fileUids` is not an array of strings. An empty `fileUids` is left
 * alone — it means "keep none of the files", not "keep all of them", and the
 * write path routes that intent to `stopKeeping` before it can be stored.
 */

import type { DesktopPin, DesktopPinScope } from '#types';

const WHOLE_DOCUMENT: DesktopPinScope = { kind: 'document' };

export const resolveDesktopPinScope = (
    pin: DesktopPin | null | undefined,
): DesktopPinScope => {
    const stored: unknown = pin?.meta?.scope;
    if (typeof stored !== 'object' || stored === null) {
        return WHOLE_DOCUMENT;
    }

    const { kind, fileUids } = stored as { kind?: unknown; fileUids?: unknown };
    if (kind !== 'files') {
        return WHOLE_DOCUMENT;
    }
    if (!Array.isArray(fileUids)
        || !fileUids.every((fileUid): fileUid is string => typeof fileUid === 'string')) {
        return WHOLE_DOCUMENT;
    }

    return { kind: 'files', fileUids: [...fileUids] };
};
